import { Box, Flex } from '@chakra-ui/react';
import { ArticleNavigationContext } from '@bridge/navigationStore';
import {
  ProductApprovalState,
  productApprovalsApi,
} from '@texas/api/endpoints/productApprovalsApi';
import { articlesApi } from '@texas/api/endpoints/articlesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useArticleContext } from '@texas/components/article/articleContext/useArticleContext';
import { articleManageEvents } from '@texas/components/article/manage/events';
import { SaveAndSync } from '@texas/components/article/variant/saveAndSync/SaveAndSync';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { productApprovalEvents } from '@texas/components/shared/productApproval/events';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleContextMetadata } from './ArticleContextMetadata';
import { ArticleActivityScope } from './ArticleActivityScope';
import { ArticleBreadcrumbPicker } from './ArticleBreadcrumbPicker';
import { ConceptBreadcrumbPicker } from './ConceptBreadcrumbPicker';
import { ClassicalBreadcrumb } from './ClassicalBreadcrumb';
import { ContextUtilityLinks } from './ContextUtilityLinks';
import { CurrentContextHeader } from './CurrentContextHeader';
import { articleUtilityItems } from './navigationItems';
import { useArticleNavigationActivity } from './useArticleNavigationActivity';
import { useClientConfiguration } from '@texas/hooks/useClientConfiguration';
import { ProductDevelopmentNavigation } from './ProductDevelopmentNavigation';
import { LegacyVariantNavigation } from './LegacyVariantNavigation';

interface ArticleContextNavigationProps {
  activeState: string;
  context: ArticleNavigationContext;
}

const EMPTY_BRANCH_IDS: number[] = [];
const PRODUCT_APPROVAL_EVENTS = [
  productApprovalEvents.productApprovalsCreated,
  productApprovalEvents.productApprovalChanged,
  productApprovalEvents.productApprovalRejected,
  productApprovalEvents.productApprovalAccepted,
  productApprovalEvents.productApprovalRevertedToPending,
];

export function ArticleContextNavigation({
  activeState,
  context,
}: ArticleContextNavigationProps) {
  const { t } = useTranslation();
  const { isProduction } = useClientConfiguration();
  const params = {
    articleId: context.article.id,
    variantId: context.currentVariantId ?? undefined,
  };
  const currentVariant = useMemo(
    () =>
      context.variants.find(
        (variant) => variant.id === context.currentVariantId,
      ),
    [context.currentVariantId, context.variants],
  );
  const productDevelopmentVariant = useMemo(
    () => context.variants.find((variant) => variant.isProductDevelopment),
    [context.variants],
  );
  const articleActivityContext = useArticleContext();
  const selectedBranchIds =
    articleActivityContext.articleId === context.article.id
      ? articleActivityContext.branchIds
      : EMPTY_BRANCH_IDS;
  const {
    data: activityBranches,
    loading: loadingActivityBranches,
    refetch: refetchActivityBranches,
  } = useApiResource(articlesApi.getArticleBranches);
  const refreshActivityBranches = useCallback(
    () => refetchActivityBranches(context.article.id),
    [context.article.id, refetchActivityBranches],
  );
  useEffect(() => {
    void refreshActivityBranches();
  }, [refreshActivityBranches]);
  const refreshActivityBranchesFromEvent = useCallback(() => {
    void refreshActivityBranches();
  }, [refreshActivityBranches]);
  useSignalSubscriptionEvents({
    triggerOnEvent: refreshActivityBranchesFromEvent,
    signalEvents: [articleManageEvents.branchesModified],
  });
  const { data: pendingProductApprovals, refetch: refetchProductApprovals } =
    useApiResource(productApprovalsApi.getAll);
  const refreshPendingProductApprovals = useCallback(
    () =>
      refetchProductApprovals({
        articleId: context.article.id,
        page: 1,
        pageSize: 1,
        searchTerm: '',
        sortBy: 'formatNumber',
        sortDesc: false,
        state: ProductApprovalState.Pending,
        branchIds: selectedBranchIds.length > 0 ? selectedBranchIds : undefined,
      }),
    [context.article.id, refetchProductApprovals, selectedBranchIds],
  );
  const refreshPendingProductApprovalsFromEvent = useCallback(() => {
    void refreshPendingProductApprovals();
  }, [refreshPendingProductApprovals]);

  useEffect(() => {
    void refreshPendingProductApprovals();
  }, [refreshPendingProductApprovals]);
  useSignalSubscriptionEvents({
    triggerOnEvent: refreshPendingProductApprovalsFromEvent,
    signalEvents: PRODUCT_APPROVAL_EVENTS,
  });
  const productDevelopmentNavigation = useArticleNavigationActivity(
    productDevelopmentVariant?.id ?? null,
    {
      articleId: context.article.id,
      showGarp: false,
      syncedVariantId: context.article.syncedVariantId ?? null,
    },
  );
  const breadcrumbItems = [
    {
      label: context.customer.name,
      route: 'customer.articles',
      navobj: { customerId: context.customer.id },
    },
    ...(context.concept
      ? [
          {
            label: context.concept.name,
            content: (
              <ConceptBreadcrumbPicker
                context={{ ...context, concept: context.concept }}
              />
            ),
          },
        ]
      : []),
    {
      label: context.article.itemCode,
      content: <ArticleBreadcrumbPicker context={context} />,
    },
  ];

  return (
    <Flex direction="column" flex={1} minH={0}>
      <ClassicalBreadcrumb items={breadcrumbItems} />
      <CurrentContextHeader
        archived={context.article.archived}
        label={t('navigation.article')}
        subtitle={
          context.article.name &&
          context.article.name !== context.article.itemCode
            ? context.article.name
            : undefined
        }
        title={context.article.itemCode}
      >
        <ArticleContextMetadata article={context.article} />
      </CurrentContextHeader>
      {context.article.basedOnId && context.article.basedOnArticleNumber && (
        <AngularNavLink
          to="article"
          navobj={{ articleId: context.article.basedOnId }}
          variant="navigationReference"
        >
          {t('navigation.basedOn', {
            articleNumber: context.article.basedOnArticleNumber,
          })}
        </AngularNavLink>
      )}
      <ContextUtilityLinks
        activeState={activeState}
        items={articleUtilityItems}
        metaByRoute={{
          'article.tasks': productDevelopmentNavigation.openTaskCount
            ? String(productDevelopmentNavigation.openTaskCount)
            : undefined,
          'article.productApproval': pendingProductApprovals?.totalItems
            ? String(pendingProductApprovals.totalItems)
            : undefined,
        }}
        navobj={params}
      />
      {context.currentVariantId && (
        <ArticleActivityScope
          articleId={context.article.id}
          branches={activityBranches ?? []}
          loading={loadingActivityBranches}
          ready={activityBranches !== null}
          variantId={context.currentVariantId}
        />
      )}
      {productDevelopmentVariant && (
        <ProductDevelopmentNavigation
          activeState={activeState}
          activity={productDevelopmentNavigation.activity}
          context={context}
          isProduction={isProduction}
          variantId={productDevelopmentVariant.id}
        />
      )}
      <LegacyVariantNavigation
        activeState={activeState}
        context={context}
        hasProductDevelopmentVariant={productDevelopmentVariant !== undefined}
      />
      <Box flex={1} minH={4} />
      {currentVariant && (
        <SaveAndSync
          articleId={context.article.id}
          variantId={currentVariant.id}
          syncedVariantId={context.article.syncedVariantId ?? undefined}
          isMain={currentVariant.isMain}
          isProductDevelopment={currentVariant.isProductDevelopment}
          isArticleArchived={context.article.archived ?? false}
          buttonProps={{
            borderRadius: 0,
            h: 9,
            ml: 0,
            mt: 4,
            w: 'full',
          }}
        />
      )}
    </Flex>
  );
}
